import { c as _c } from "react/compiler-runtime";
import { IconAsterisk, IconBan, IconChevronRight, IconCircleCheck, IconCircleHalf2, IconClockPause, IconDotsVertical, IconExternalLink, IconFilter, IconHelp, IconWaveSquare, IconX } from "@tabler/icons-react";
import { useDeferredValue, useMemo, useState } from "react";
import { PillCount } from "@/components/domain/pill-count";
import { filterRobotsGroups, type RobotsGroupSummary, type RobotsRuleFilter, type RobotsSummaryData, summarizeRobots } from "@/lib/robots";
import type { RobotsRule, SeoResponse } from "@domainstack/types";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@domainstack/ui/accordion";
import { Button } from "@domainstack/ui/button";
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@domainstack/ui/empty";
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from "@domainstack/ui/input-group";
import { ToggleGroup, ToggleGroupItem } from "@domainstack/ui/toggle-group";
import { Tooltip, TooltipContent, TooltipTrigger } from "@domainstack/ui/tooltip";
import { cn } from "@domainstack/ui/utils";
const RULES_PREVIEW = 6;
const SITEMAPS_PREVIEW = 2;
const REVEAL_CLASS = "animate-in duration-200 ease-out fade-in-0 motion-reduce:animate-none";
const EMPTY_SUMMARY: RobotsSummaryData = {
  groups: [],
  counts: {
    allow: 0,
    disallow: 0
  }
};
function getRuleItems(rules: RobotsRule[]) {
  const seen = new Map<string, number>();
  return rules.map(rule => {
    const baseKey = `${rule.type}-${rule.value}`;
    const duplicateCount = seen.get(baseKey) ?? 0;
    seen.set(baseKey, duplicateCount + 1);
    return {
      key: `${baseKey}-${duplicateCount}`,
      rule
    };
  });
}
function highlight(text: string, q: string) {
  if (!q) return text;
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return text;
  const before = text.slice(0, idx);
  const match = text.slice(idx, idx + q.length);
  const after = text.slice(idx + q.length);
  return <>
      {before}
      <span className="bg-yellow-500/30 dark:bg-yellow-400/30">{match}</span>
      {after}
    </>;
}
export function RobotsSummary(t0) {
  const $ = _c(45);
  const {
    domain,
    robots
  } = t0;
  let t1;
  if ($[0] !== robots) {
    t1 = robots ? summarizeRobots(robots) : EMPTY_SUMMARY;
    $[0] = robots;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const summary = t1;
  const {
    counts
  } = summary;
  const listedCount = counts.allow + counts.disallow;
  let t2;
  if ($[2] !== robots?.sitemaps) {
    t2 = robots?.sitemaps ?? [];
    $[2] = robots?.sitemaps;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const sitemaps = t2;
  const [query, setQuery] = useState("");
  const [only, setOnly] = useState("all");
  const t3 = useDeferredValue(query);
  let t4;
  if ($[4] !== t3) {
    t4 = t3.trim();
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const q = t4;
  const isSearching = q !== "";
  let t5;
  if ($[6] !== only || $[7] !== q || $[8] !== summary.groups) {
    t5 = filterRobotsGroups(summary.groups, {
      query: q,
      only
    });
    $[6] = only;
    $[7] = q;
    $[8] = summary.groups;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const groups = t5;
  let t6;
  if ($[10] !== summary.groups[0]) {
    t6 = () => {
      const first = summary.groups[0];
      return first?.userAgents[0] === "*" ? [first.key] : [];
    };
    $[10] = summary.groups[0];
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const [openGroups, setOpenGroups] = useState(t6);
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setQuery("");
      setOnly("all");
    };
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const resetFilters = t7;
  const t8 = `https://${domain}/robots.txt`;
  let t10;
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <span>robots.txt</span>;
    t10 = <IconExternalLink className="relative bottom-px inline-flex size-3" aria-hidden={true} />;
    $[13] = t10;
    $[14] = t9;
  } else {
    t10 = $[13];
    t9 = $[14];
  }
  let t11;
  if ($[15] !== t8) {
    t11 = <a href={t8} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:underline hover:underline-offset-3">{t9}{t10}</a>;
    $[15] = t8;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== listedCount) {
    t12 = <PillCount count={listedCount} color="blue" />;
    $[17] = listedCount;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== t11 || $[20] !== t12) {
    t13 = <div className="mt-5 flex items-center gap-2 text-[11px] leading-none tracking-[0.08em] text-foreground/70 uppercase dark:text-foreground/80">{t11}{t12}</div>;
    $[19] = t11;
    $[20] = t12;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== counts.allow || $[23] !== counts.disallow || $[24] !== listedCount || $[25] !== only || $[26] !== query) {
    t14 = listedCount > 0 ? <div className="flex flex-col gap-2 sm:flex-row sm:items-center"><InputGroup className="sm:flex-1"><InputGroupInput name="robots-filter" placeholder={"Filter rules\u2026"} value={query} onChange={e => setQuery(e.currentTarget.value)} aria-label="Filter robots rules" autoComplete="off" autoCorrect="off" autoCapitalize="none" spellCheck={false} /><InputGroupAddon><IconFilter aria-hidden={true} /></InputGroupAddon>{query ? <InputGroupAddon align="inline-end"><InputGroupButton size="sm" variant="ghost" onClick={() => setQuery("")} aria-label="Clear filter"><IconX aria-hidden={true} /></InputGroupButton></InputGroupAddon> : null}</InputGroup><ToggleGroup multiple={false} value={[only]} onValueChange={groupValue => {
        const next = groupValue[0] as RobotsRuleFilter | undefined;
        setOnly(next ?? "all");
      }} spacing={1} className="relative h-9 w-full items-stretch overflow-hidden rounded-lg border bg-muted/40 p-1 text-muted-foreground sm:w-auto [&>*]:flex-1 sm:[&>*]:flex-none"><ToggleGroupItem value="all" className="h-full"><IconCircleHalf2 className="size-3.5 text-accent-blue" aria-hidden={true} /><span className="text-[13px]">All</span><PillCount count={listedCount} color="slate" /></ToggleGroupItem><ToggleGroupItem value="allow" className="h-full"><IconCircleCheck className="size-3.5 text-accent-green" aria-hidden={true} /><span className="text-[13px]">Allow</span><PillCount count={counts.allow} color="slate" /></ToggleGroupItem><ToggleGroupItem value="disallow" className="h-full"><IconBan className="size-3.5 text-destructive" aria-hidden={true} /><span className="text-[13px]">Disallow</span><PillCount count={counts.disallow} color="slate" /></ToggleGroupItem></ToggleGroup></div> : null;
    $[22] = counts.allow;
    $[23] = counts.disallow;
    $[24] = listedCount;
    $[25] = only;
    $[26] = query;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== groups || $[29] !== isSearching || $[30] !== only || $[31] !== openGroups || $[32] !== q || $[33] !== sitemaps.length || $[34] !== summary.groups.length) {
    t15 = groups.length > 0 ? <Accordion hiddenUntilFound={true} multiple={isSearching} value={isSearching ? groups.map(_temp) : openGroups} onValueChange={value => {
      if (!isSearching) {
        setOpenGroups(value);
      }
    }}>{groups.map(group => <RobotsGroupItem key={group.key} group={group} query={q} only={only} />)}</Accordion> : summary.groups.length > 0 ? <div className="text-sm text-muted-foreground">No matching rules.<Button variant="link" className="px-1" onClick={resetFilters}>Reset filters</Button></div> : sitemaps.length > 0 ? <Empty className="border border-solid bg-background/60"><EmptyHeader><EmptyMedia variant="icon"><IconHelp aria-hidden={true} /></EmptyMedia><EmptyTitle>No Crawl Rules Detected</EmptyTitle><EmptyDescription>This website’s robots.txt only declares sitemaps; no crawl rules are specified.</EmptyDescription></EmptyHeader></Empty> : null;
    $[28] = groups;
    $[29] = isSearching;
    $[30] = only;
    $[31] = openGroups;
    $[32] = q;
    $[33] = sitemaps.length;
    $[34] = summary.groups.length;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== sitemaps) {
    t16 = sitemaps.length > 0 ? <SitemapsList items={sitemaps} /> : null;
    $[36] = sitemaps;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t14 || $[39] !== t15 || $[40] !== t16) {
    t17 = <div className="space-y-4">{t14}{t15}{t16}</div>;
    $[38] = t14;
    $[39] = t15;
    $[40] = t16;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== t13 || $[43] !== t17) {
    t18 = <div className="space-y-4">{t13}{t17}</div>;
    $[42] = t13;
    $[43] = t17;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  return t18;
}
function _temp(g) {
  return g.key;
}
function RobotsGroupItem(t0) {
  const $ = _c(25);
  const {
    group,
    query,
    only
  } = t0;
  let t1;
  if ($[0] !== group.rules) {
    t1 = group.rules.filter(_temp2);
    $[0] = group.rules;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const allowN = t1.length;
  let t2;
  if ($[2] !== group.rules) {
    t2 = group.rules.filter(_temp3);
    $[2] = group.rules;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const disallowN = t2.length;
  const t3 = group.key;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = cn("border-b-0", REVEAL_CLASS);
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <IconChevronRight className="size-3 text-muted-foreground transition-transform group-data-[panel-open]/accordion:rotate-90 motion-reduce:transition-none" aria-hidden={true} />;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== group.userAgents) {
    t6 = group.userAgents.map(_temp4);
    $[6] = group.userAgents;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t6) {
    t7 = <div className="flex min-w-0 flex-wrap items-center gap-1.5">{t5}{t6}</div>;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const t8 = only !== "disallow" && `${allowN} allow`;
  const t9 = only !== "allow" && `${disallowN} disallow`;
  let t10;
  if ($[10] !== t8 || $[11] !== t9) {
    t10 = [t8, t9].filter(Boolean);
    $[10] = t8;
    $[11] = t9;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  const t11 = t10.join(" \xB7 ");
  let t12;
  if ($[13] !== t11) {
    t12 = <div className="shrink-0 pt-1 text-xs leading-none whitespace-nowrap text-muted-foreground">{t11}</div>;
    $[13] = t11;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  let t13;
  if ($[15] !== t12 || $[16] !== t7) {
    t13 = <AccordionTrigger className="group/accordion items-start px-2 py-2 hover:bg-accent/35 hover:no-underline data-[panel-open]:pr-2 [&>svg]:hidden"><div className="flex w-full items-start justify-between gap-3">{t7}{t12}</div></AccordionTrigger>;
    $[15] = t12;
    $[16] = t7;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] !== group || $[19] !== query) {
    t14 = <AccordionContent className="pb-2"><GroupRules group={group} query={query} /></AccordionContent>;
    $[18] = group;
    $[19] = query;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] !== group.key || $[22] !== t13 || $[23] !== t14) {
    t15 = <AccordionItem value={t3} className={t4}>{t13}{t14}</AccordionItem>;
    $[21] = group.key;
    $[22] = t13;
    $[23] = t14;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  return t15;
}
function _temp4(ua) {
  return <span key={ua} className={cn("flex items-center gap-1 rounded px-1.5 py-1 text-xs leading-none whitespace-nowrap", ua === "*" ? "bg-accent-purple/18 text-accent-purple" : "bg-muted")}>{ua === "*" ? <><IconAsterisk className="size-3" aria-hidden={true} />All bots</> : ua}</span>;
}
function _temp3(r_0) {
  return r_0.type === "disallow";
}
function _temp2(r) {
  return r.type === "allow";
}
function GroupRules(t0) {
  const $ = _c(26);
  const {
    group,
    query
  } = t0;
  const [expanded, setExpanded] = useState(false);
  let more;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== expanded || $[1] !== group.hasEmptyAllow || $[2] !== group.hasEmptyDisallow || $[3] !== group.rules || $[4] !== query) {
    const ruleItems = getRuleItems(group.rules);
    const shown = query || expanded ? ruleItems : ruleItems.slice(0, RULES_PREVIEW);
    more = ruleItems.length - shown.length;
    t1 = "flex flex-col py-2";
    if ($[10] !== group.hasEmptyDisallow || $[11] !== group.rules.length) {
      t2 = group.rules.length === 0 && group.hasEmptyDisallow ? <div className="rounded-md bg-muted/30 px-2 py-1 text-[13px] text-muted-foreground/90">No disallow restrictions (allow all)</div> : null;
      $[10] = group.hasEmptyDisallow;
      $[11] = group.rules.length;
      $[12] = t2;
    } else {
      t2 = $[12];
    }
    if ($[13] !== group.hasEmptyAllow || $[14] !== group.rules.length) {
      t3 = group.rules.length === 0 && group.hasEmptyAllow ? <div className="rounded-md bg-muted/30 px-2 py-1 text-[13px] text-muted-foreground/90">No explicit allow paths</div> : null;
      $[13] = group.hasEmptyAllow;
      $[14] = group.rules.length;
      $[15] = t3;
    } else {
      t3 = $[15];
    }
    let t5;
    if ($[16] !== query) {
      t5 = (t6, i) => {
        const {
          key,
          rule
        } = t6;
        return <div key={key} className={cn("flex items-center gap-2 border-t border-muted px-2 py-2.5 font-mono text-xs first:border-t-0", i >= RULES_PREVIEW && REVEAL_CLASS)}><RuleTypeDot type={rule.type} /><span className="truncate">{highlight(rule.value, query)}</span></div>;
      };
      $[16] = query;
      $[17] = t5;
    } else {
      t5 = $[17];
    }
    t4 = shown.map(t5);
    $[0] = expanded;
    $[1] = group.hasEmptyAllow;
    $[2] = group.hasEmptyDisallow;
    $[3] = group.rules;
    $[4] = query;
    $[5] = more;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    more = $[5];
    t1 = $[6];
    t2 = $[7];
    t3 = $[8];
    t4 = $[9];
  }
  let t5;
  if ($[18] !== more) {
    t5 = more > 0 ? <div className="mt-1 flex justify-start"><Button size="sm" variant="outline" className="text-[12px]" onClick={() => setExpanded(true)}><IconDotsVertical className="!size-3.5" aria-hidden={true} /><span>Show {more} more</span></Button></div> : null;
    $[18] = more;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== t1 || $[21] !== t2 || $[22] !== t3 || $[23] !== t4 || $[24] !== t5) {
    t6 = <div className={t1}>{t2}{t3}{t4}{t5}</div>;
    $[20] = t1;
    $[21] = t2;
    $[22] = t3;
    $[23] = t4;
    $[24] = t5;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  return t6;
}
const ruleTypeConfig = {
  allow: {
    Icon: IconCircleCheck,
    label: "Allow",
    colorClass: "text-accent-green"
  },
  disallow: {
    Icon: IconBan,
    label: "Disallow",
    colorClass: "text-destructive"
  },
  crawlDelay: {
    Icon: IconClockPause,
    label: "Crawl delay",
    colorClass: "text-accent-orange"
  },
  contentSignal: {
    Icon: IconWaveSquare,
    label: "Content signal",
    colorClass: "text-accent-purple"
  }
} as const;
function RuleTypeDot(t0) {
  const $ = _c(13);
  const {
    type
  } = t0;
  const {
    Icon,
    label,
    colorClass
  } = ruleTypeConfig[type];
  let t1;
  if ($[0] !== colorClass) {
    t1 = cn("size-3.5", colorClass);
    $[0] = colorClass;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== Icon || $[3] !== t1) {
    t2 = <Icon className={t1} aria-hidden={true} />;
    $[2] = Icon;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== label || $[6] !== t2) {
    t3 = <TooltipTrigger render={<button type="button" className="flex size-4 items-center justify-center" aria-label={label}>{t2}</button>} />;
    $[5] = label;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== label) {
    t4 = <TooltipContent side="left" sideOffset={8}>{label}</TooltipContent>;
    $[8] = label;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3 || $[11] !== t4) {
    t5 = <Tooltip>{t3}{t4}</Tooltip>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
}
function SitemapsList(t0) {
  const $ = _c(16);
  const {
    items
  } = t0;
  const [expanded, setExpanded] = useState(false);
  let t1;
  if ($[0] !== expanded || $[1] !== items) {
    t1 = expanded ? items : items.slice(0, SITEMAPS_PREVIEW);
    $[0] = expanded;
    $[1] = items;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const shown = t1;
  const more = items.length - shown.length;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span>Sitemaps</span>;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== items.length) {
    t3 = <div className="flex items-center gap-2 text-[11px] leading-none tracking-[0.08em] text-foreground/70 uppercase dark:text-foreground/80">{t2}<PillCount count={items.length} color="green" /></div>;
    $[4] = items.length;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== shown) {
    t4 = shown.map(_temp5);
    $[6] = shown;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== more) {
    t5 = more > 0 ? <div className="mt-1 flex justify-start"><Button size="sm" variant="outline" className="text-[12px]" onClick={() => setExpanded(true)}><IconDotsVertical className="!size-3.5" aria-hidden={true} /><span>Show {more} more</span></Button></div> : null;
    $[8] = more;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t4 || $[11] !== t5) {
    t6 = <div className="flex flex-col gap-2.5">{t4}{t5}</div>;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t3 || $[14] !== t6) {
    t7 = <div className="space-y-3">{t3}{t6}</div>;
    $[13] = t3;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
}
function _temp5(url, i) {
  return <div key={url} className={cn("flex items-center", i >= SITEMAPS_PREVIEW && REVEAL_CLASS)}><a className="flex items-center gap-1.5 truncate text-[13px] font-medium text-foreground/85 hover:text-foreground/60 hover:no-underline" href={url} target="_blank" rel="noopener">{url}<IconExternalLink className="size-3" aria-hidden={true} /></a></div>;
}